import { Heading, Icon, Page, Text, TextLink } from '@ui/themed';
import { t } from 'i18next';
import { useEffect } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { INTERCOM_TRIGGER } from '../../../constants/intercom';
import { useSite, withPageProviders } from '../../../context/page-context';
import { track } from '../../../lib/mixpanel';
import { DevelopmentPlanBanner } from '../components/development-plan-banner';
import { PlansGrid, PlansGridSkeleton } from '../components/plans-grid';
import { useAvailablePlans } from '../hooks/use-available-plans';

type BillingSelectPlanPageProps = {
  isDevelopmentStore: boolean;
};

function BillingSelectPlanPageInnerContent({ isDevelopmentStore }: BillingSelectPlanPageProps) {
  const { data: availablePlans, isLoading, isError, refetch } = useAvailablePlans();

  if (isError) {
    return (
      <SparkSuspense
        isLoading={false}
        isError={isError}
        errorMessage={t('features:billing.pages.errors.load_plans')}
        onReload={() => void refetch()}
      />
    );
  }

  if (isLoading) {
    return <PlansGridSkeleton />;
  }

  if (!availablePlans) {
    return null;
  }

  return (
    <>
      {isDevelopmentStore && <DevelopmentPlanBanner />}
      <PlansGrid availablePlans={availablePlans} canTrial hideEnterprise />
      <div className="flex flex-nowrap items-center justify-center gap-1 text-center">
        <Text variant="secondary" size="sm">
          {t('features:billing.pages.select_plan.need_help')}
        </Text>
        <span className={INTERCOM_TRIGGER.onboardingChat}>
          <TextLink size="sm" variant="secondary" onClick={() => {}}>
            {t('features:billing.pages.select_plan.contact_support')}
          </TextLink>
        </span>
      </div>
    </>
  );
}

function BillingSelectPlanPageContent(props: BillingSelectPlanPageProps) {
  const site = useSite();
  useEffect(() => {
    track(site, 'Billing Select Plan Page Viewed', {
      is_development_store: props.isDevelopmentStore,
    });
  }, [site, props.isDevelopmentStore]);

  return (
    <Page>
      <div className="flex flex-col items-center gap-1 text-center">
        <Heading level="h1" style="xl">
          {t('features:billing.pages.select_plan.title')}
        </Heading>
        <div className="flex w-fit flex-row justify-between gap-3">
          <div className="flex flex-row items-center gap-0.5">
            <Icon name="payment" />{' '}
            <Text size="md" variant="secondary">
              {t('features:billing.pages.select_plan.free_trial')}
            </Text>
          </div>
          <div className="flex flex-row items-center gap-0.5">
            <Icon name="calendar" />{' '}
            <Text size="md" variant="secondary">
              {t('features:billing.pages.select_plan.cancel_any_time')}
            </Text>
          </div>
          <div className="flex flex-row items-center gap-0.5">
            <Icon name="premium" />{' '}
            <Text size="md" variant="secondary">
              {t('features:billing.pages.select_plan.expert_support')}
            </Text>
          </div>
        </div>
      </div>
      <BillingSelectPlanPageInnerContent {...props} />
    </Page>
  );
}

export const BillingSelectPlanPage = withPageProviders(BillingSelectPlanPageContent);
